import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { PURCHASE_TICKETS, GET_MY_TICKETS } from '../../graphql/raffle';
import { useCountdown } from './useCountdown';
import GButton from '../../components/ui/base/GButton';
import useMainStore from '../../store/mainState';

const prizeImages: Record<string, string> = {
    CAR: require('../../assets/images/car.png'),
    IPHONE: require('../../assets/images/iphone.png'),
    PHONE: require('../../assets/images/iphone.png'),
};

interface RaffleDetailProps {
    ticket: any;
    period: string;
    onBack: () => void;
}

const periodStyles: Record<string, { gradient: string; accent: string }> = {
    daily: { gradient: 'from-[#0a1628] via-[#0d2a52] to-[#1a3a6a]', accent: '#4DBDFF' },
    weekly: { gradient: 'from-[#0a1628] via-[#1a2a15] to-[#2a5741]', accent: '#4DFF8F' },
    monthly: { gradient: 'from-[#1a1020] via-[#2a1525] to-[#5e2627]', accent: '#FF9F4D' },
};

const RaffleDetail = ({ ticket, period, onBack }: RaffleDetailProps) => {
    const { t } = useTranslation();
    const { user } = useMainStore();
    const style = periodStyles[period.toLowerCase()] || periodStyles.daily;
    const { days, hours, minutes, seconds } = useCountdown(ticket.expiresAt);

    const meta = ticket.meta ? (typeof ticket.meta === 'string' ? JSON.parse(ticket.meta) : ticket.meta) : {};
    const price = meta.ticketPrice ?? meta.price ?? 0;
    const isFree = meta.isFree === true || price === 0;
    const prizeImage = meta.prizeType ? prizeImages[meta.prizeType.toUpperCase()] : null;

    const [quantity, setQuantity] = useState(1);
    const [purchasing, setPurchasing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const [purchaseTickets] = useMutation(PURCHASE_TICKETS, { errorPolicy: 'all' });
    const { data: myTicketsData, refetch: refetchMyTickets } = useQuery(GET_MY_TICKETS, { skip: !user });

    // Get tickets for this period
    const myPeriodTickets = myTicketsData?.myTickets
        ?.filter((g: any) => g.period.toLowerCase() === period.toLowerCase())
        ?.flatMap((g: any) => g.tickets || []) || [];

    const handlePurchase = async (free: boolean) => {
        setPurchasing(true);
        setMessage(null);
        try {
            const result = await purchaseTickets({
                variables: {
                    bonusTypeId: Number(ticket.id),
                    quantity: free ? 1 : quantity,
                    isFree: free,
                },
            });
            if (result.errors && result.errors.length > 0) {
                setMessage({ type: 'error', text: result.errors[0].message });
            } else {
                setMessage({ type: 'success', text: free ? t('raffle.freeTicketClaimed') : `${quantity} ${t('raffle.ticketsPurchased')}` });
                refetchMyTickets();
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err?.message || t('raffle.failedToPurchase') });
        }
        setPurchasing(false);
    };

    return (
        <div className="pb-[40px]">
            {/* Back button */}
            <button onClick={onBack} className="flex items-center gap-[8px] text-white/40 hover:text-white/70 transition-colors duration-200 mb-[20px] group">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover:-translate-x-[3px]">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                <span className="text-[14px] font-semibold">{t('raffle.backToDraws')}</span>
            </button>

            {/* Hero card */}
            <div className={`rounded-[20px] overflow-hidden bg-gradient-to-r ${style.gradient} border border-white/[0.08] mb-[30px]`}>
                <div className="p-[24px] md:p-[36px]">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-[24px]">
                        {/* Left */}
                        <div className="flex items-center gap-[20px]">
                        {prizeImage && (
                            <img src={prizeImage} alt={meta.prizeType} className="w-[120px] h-[90px] md:w-[180px] md:h-[130px] object-contain flex-shrink-0" />
                        )}
                        <div>
                            <span className="text-[12px] font-bold uppercase tracking-widest mb-[8px] block" style={{ color: style.accent }}>{period} {t('raffle.draw')}</span>
                            <h1 className="text-[28px] md:text-[36px] font-extrabold text-white tracking-tight mb-[6px]">{ticket.name}</h1>
                            <p className="text-white/30 text-[14px]">{t('raffle.code')}: {ticket.code}</p>
                        </div>
                        </div>
                        {/* Countdown */}
                        <div className="flex flex-col items-center md:items-end">
                            <span className="text-white/40 text-[12px] font-semibold mb-[8px]">{t('raffle.timeRemaining')}</span>
                            <div className="flex gap-[8px]">
                                {[
                                    { value: days, label: t('raffle.days') },
                                    { value: hours, label: t('raffle.hours') },
                                    { value: minutes, label: t('raffle.min') },
                                    { value: seconds, label: t('raffle.sec') },
                                ].map((item) => (
                                    <div key={item.label} className="flex flex-col items-center">
                                        <div className="w-[52px] h-[52px] rounded-[12px] bg-white/[0.08] border border-white/[0.1] flex items-center justify-center" style={{ color: style.accent }}>
                                            <span className="text-[22px] font-bold">{String(item.value).padStart(2, '0')}</span>
                                        </div>
                                        <span className="text-white/30 text-[10px] mt-[4px] font-medium">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Purchase Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] mb-[30px]">
                {/* Free ticket */}
                {isFree && (
                    <div className="rounded-[16px] bg-white/[0.04] border border-white/[0.08] p-[24px]">
                        <div className="flex items-center gap-[10px] mb-[16px]">
                            <div className="w-[40px] h-[40px] rounded-[10px] flex items-center justify-center text-[20px]" style={{ backgroundColor: `${style.accent}15` }}>
                                🎁
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-[16px]">{t('raffle.freeTicket')}</h3>
                                <p className="text-white/30 text-[12px]">{t('raffle.claimFreePerDraw')}</p>
                            </div>
                        </div>
                        <GButton
                            className="w-full py-[14px] rounded-[12px]"
                            onClick={() => handlePurchase(true)}
                            disabled={purchasing}
                        >
                            {purchasing ? t('raffle.claiming') : t('raffle.claimFreeTicket')}
                        </GButton>
                    </div>
                )}

                {/* Buy tickets */}
                <div className="rounded-[16px] bg-white/[0.04] border border-white/[0.08] p-[24px]">
                    <div className="flex items-center justify-between mb-[16px]">
                        <div className="flex items-center gap-[10px]">
                            <div className="w-[40px] h-[40px] rounded-[10px] flex items-center justify-center text-[20px]" style={{ backgroundColor: `${style.accent}15` }}>
                                🎟️
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-[16px]">{t('raffle.buyTickets')}</h3>
                                <p className="text-white/30 text-[12px]">{t('raffle.selectQuantity')}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-white/30 text-[11px] font-medium">{t('raffle.pricePerTicket')}</p>
                            <p className="text-white font-bold text-[18px]" style={{ color: style.accent }}>${price.toFixed(2)}</p>
                        </div>
                    </div>

                    {/* Quantity selector */}
                    <div className="flex items-center gap-[12px] mb-[16px]">
                        <span className="text-white/40 text-[13px] font-semibold">{t('raffle.qty')}:</span>
                        <div className="flex items-center bg-white/[0.06] rounded-[10px] border border-white/[0.1]">
                            <button
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                className="w-[40px] h-[40px] flex items-center justify-center text-white/60 hover:text-white text-[20px] font-bold transition-colors"
                            >−</button>
                            <span className="w-[48px] text-center text-white font-bold text-[16px]">{quantity}</span>
                            <button
                                onClick={() => setQuantity(quantity + 1)}
                                className="w-[40px] h-[40px] flex items-center justify-center text-white/60 hover:text-white text-[20px] font-bold transition-colors"
                            >+</button>
                        </div>
                    </div>

                    {/* Total */}
                    <div className="flex items-center justify-between bg-white/[0.04] rounded-[10px] border border-white/[0.06] px-[16px] py-[12px] mb-[16px]">
                        <span className="text-white/40 text-[14px] font-medium">{quantity} ticket{quantity > 1 ? 's' : ''} × ${price.toFixed(2)}</span>
                        <div className="text-right">
                            <p className="text-white/30 text-[11px]">{t('raffle.total')}</p>
                            <p className="text-white font-bold text-[22px]">${(price * quantity).toFixed(2)}</p>
                        </div>
                    </div>

                    <GButton
                        className="w-full py-[14px] rounded-[12px]"
                        variant="gold"
                        onClick={() => handlePurchase(false)}
                        disabled={purchasing}
                    >
                        {purchasing ? t('raffle.processing') : `${t('raffle.buyTickets')} ${quantity} — $${(price * quantity).toFixed(2)}`}
                    </GButton>
                </div>
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-[24px] px-4 py-3 rounded-[12px] text-[14px] font-semibold animate-[fadeIn_0.3s_ease-out] ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                    {message.text}
                </div>
            )}

            {/* My Tickets for this draw */}
            <div>
                <div className="flex items-center gap-[10px] mb-[16px]">
                    <div className="w-[3px] h-[20px] bg-gradient-to-b from-[#4DBDFF] to-[#0086D3] rounded-full"></div>
                    <h2 className="text-[18px] font-bold text-white tracking-tight">{t('raffle.yourTickets')}</h2>
                    {myPeriodTickets.length > 0 && (
                        <span className="text-[12px] font-bold text-[#0086D3] bg-[#0086D3]/10 px-[8px] py-[2px] rounded-[6px]">
                            {myPeriodTickets.reduce((sum: number, t: any) => sum + (t.totalTickets || 1), 0)}
                        </span>
                    )}
                </div>

                {myPeriodTickets.length === 0 ? (
                    <div className="rounded-[16px] bg-white/[0.03] border border-white/[0.06] p-[40px] text-center">
                        <p className="text-white/20 text-[14px]">{t('raffle.noTicketsYet')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-[10px]">
                        {myPeriodTickets.map((t: any, i: number) => (
                            <div
                                key={`${t.code}-${i}`}
                                className="rounded-[12px] bg-white/[0.04] border border-white/[0.08] p-[12px] flex flex-col items-center gap-[6px] hover:bg-white/[0.08] transition-all duration-200"
                            >
                                <div className="w-[36px] h-[36px] rounded-[8px] flex items-center justify-center text-[16px]" style={{ backgroundColor: `${style.accent}15`, color: style.accent }}>
                                    🎟️
                                </div>
                                <span className="text-white text-[10px] font-semibold text-center truncate w-full">{t.name}</span>
                                {t.totalTickets > 1 && (
                                    <span className="text-white/30 text-[9px] font-bold">x{t.totalTickets}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RaffleDetail;
